import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Dropdown, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ActionTaskTypes,
  ExecutionItem,
  ExecutionItemStatuses,
  ExecutionLink,
  Result,
} from '../../../interfaces/changeRequest';
import './ChangeRequestActions.less';
import NoDataFound from '../../NoDataFound/NoDataFound';
import {
  MoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { RootState } from '../../../app/store';
import { useSelector } from 'react-redux';
import {
  useAgentSelector,
  useAgentDispatch,
} from '../../../hooks/useAgentStore';
import SanitizedMarkdown from '../../SanitizedMarkdown/SanitizedMarkdown';
import {
  currentActionItemId,
  handleImplementRow,
  IMPLEMENTABLE_STATUSES,
  isOwnChangeRequest,
} from '../../../services/changeRequestService';
import { setRevokeAPICalls } from '../../../app/Slices/changeRequestStateSlice';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';
import ErrorResult from '../../ErrorResult/ErrorResult';
import { ChangeRequestSource } from '../../../enums/changeRequest';
import EditChangeRequestAction from '../EditChangeRequest/EditChangeRequestAction/EditChangeRequestAction';
import { useChangeRequest } from '../../../hooks/useChangeRequest';
import { SerializableError } from '../../../utils/errorHandling';
import { ItemType } from 'antd/es/menu/interface';
import { Notify } from '../../../services/notificationService';
import { getDisabledReason } from '../../../utils/ChangeRequestActionUtility';
import * as subscriptionService from '../../../services/subscription';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableRow, { DraggableRowProps } from '../DraggableRow/DraggableRow';
import 'react-resizable/css/styles.css';
import ResizableTitle from '../ResizableTitle/ResizableTitle';
import useResizableColumns from '../../../hooks/useResizableColumns';
import { isSameRecord } from '../../../utils/tableUtils';
import { NOT_OWN_CHANGE_REQUEST } from '../../../constants/texts';

interface ChangeRequestActionsProps {
  isPrintView?: boolean;
}

const ChangeRequestActions: React.FC<ChangeRequestActionsProps> = ({
  isPrintView = false,
}) => {
  // Global store for shared state
  const org = useSelector((state: RootState) => state.organization.org);
  const status = useSelector(selectSubscriptionStatus);

  // Agent store for change request state
  const { data, error, loading, source, isImplementing } = useAgentSelector(
    (state) => state.changeRequest
  );
  const cr = useMemo(() => {
    return data?.result;
  }, [data?.result]);
  const { currentStep } = useAgentSelector((state) => state.changeRequestSteps);
  const changeRequestId = useAgentSelector(
    (state) => state.changeRequestState.changeRequestId
  );
  const [dataSource, setDataSource] = useState<ExecutionItem[]>([]);
  const isUserReadonly = subscriptionService.isUserReadonly(status);
  const isCrReadOnly = !cr || !isOwnChangeRequest(org, cr);
  const isReadonly = isCrReadOnly || isUserReadonly;
  const readonlyMsg = isCrReadOnly
    ? NOT_OWN_CHANGE_REQUEST
    : subscriptionService.getReadonlyElementTitle();

  const isSummaryStep = currentStep === 2 || isPrintView;

  useEffect(() => {
    setDataSource(cr?.execution?.items ?? []);
  }, [data]);

  const {
    editingItem,
    isDirty,
    setIsDirty,
    navigateToEdit,
    handleEditRow,
    handleDeleteRow,
    updateDataArray,
    saveItem,
    tblRef,
    handleExpandToFullScreen,
  } = useChangeRequest<ExecutionItem>(
    cr?.execution?.items,
    source,
    loading,
    ChangeRequestSource.EDIT_ACTIONS_EXECUTION,
    '/execution'
  );
  const agentDispatch = useAgentDispatch();

  const sourceApplicable = useMemo(
    () =>
      !source ||
      source === ChangeRequestSource.ACTIONS_EXECUTION ||
      source === ChangeRequestSource.EDIT_ACTIONS_EXECUTION,
    [source]
  );
  const _loading = sourceApplicable && loading;
  const _error = (sourceApplicable && error) as SerializableError | false;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const processingRowRef = useRef<HTMLDivElement | null>(null);

  const dropdownMenu = (record: ExecutionItem, index: number): ItemType[] => [
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Edit row</span>
        </Tooltip>
      ),
      key: 'edit',
      onClick: (info) => {
        info.domEvent.stopPropagation();
        handleEditRow(record);
      },
      disabled: isImplementing || isReadonly,
    },
    {
      label: (
        <Tooltip title={readonlyMsg} placement="right" destroyOnHidden={false}>
          <span className="w-100">Delete row</span>
        </Tooltip>
      ),
      key: 'delete',
      onClick: (info) => {
        info.domEvent.stopPropagation();
        handleDeleteRow(record);
      },
      disabled: isImplementing || isReadonly,
    },
    {
      label: (
        <Tooltip
          title={
            isReadonly ? readonlyMsg : getDisabledReason(record, isImplementing)
          }
          placement="right"
          destroyOnHidden={false}
        >
          <span className="w-100">
            {record.status === 'error' || record.status === 'input needed'
              ? 'Retry Implementation'
              : 'Implement'}
          </span>
        </Tooltip>
      ),
      key: 'implement',
      onClick: (info) => {
        info.domEvent.stopPropagation();
        if (!changeRequestId) {
          Notify.error('No change request selected');
          return;
        }
        agentDispatch(setRevokeAPICalls(false));
        cr &&
          handleImplementRow(
            agentDispatch,
            cr,
            index,
            changeRequestId,
            record,
            ChangeRequestSource.ACTIONS_IMPLEMENT_ONE
          );
      },
      disabled:
        isImplementing ||
        isReadonly ||
        record.type === 'manual task' ||
        !IMPLEMENTABLE_STATUSES.includes(record.status),
    },
  ];

  const columns = useMemo(() => {
    const totalWidth = 1200;
    const fixedWidth = 75 * 2;
    const remainingWidth = totalWidth - fixedWidth;

    const actionColumns: ColumnsType<ExecutionItem> = [
      {
        title: 'S No.',
        dataIndex: 'id',
        key: 'sno',
        width: 75,
        render: (_value, _record, index) => {
          return (
            <div className="dnd-handle-icon">
              <HolderOutlined />
              {index + 1}
            </div>
          );
        },
      },
      {
        title: 'Item',
        dataIndex: 'item',
        key: 'item',
        width: (140 / 920) * remainingWidth,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: (120 / 920) * remainingWidth,
        ...(!isSummaryStep
          ? {
              filters: [
                { text: 'Automated task', value: 'automated task' },
                { text: 'Manual task', value: 'manual task' },
              ],
              onFilter: (value, record) => record.type === value,
            }
          : {}),
        render: (task: ActionTaskTypes) => {
          let color = 'purple';
          switch (task) {
            case 'manual task':
              color = 'cyan';
              break;
          }
          return (
            <Tag color={color} className="action-task">
              {task}
            </Tag>
          );
        },
      },
      {
        title: 'Details',
        dataIndex: 'details',
        key: 'details',
        width: (190 / 920) * remainingWidth,
        render: (details: string) => (
          <SanitizedMarkdown inputSrc={details} isPrintView={isPrintView} />
        ),
      },
      {
        title: 'Results',
        dataIndex: 'results',
        key: 'results',
        width: (190 / 920) * remainingWidth,
        render: (results: string) => (
          <SanitizedMarkdown inputSrc={results} isPrintView={isPrintView} />
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: (120 / 920) * remainingWidth,
        // Add status filter only if it is not the summary step.
        ...(!isSummaryStep
          ? {
              filters: [
                { text: 'Pending', value: 'pending' },
                { text: 'Input needed', value: 'input needed' },
                { text: 'Invalid', value: 'invalid' },
                { text: 'Skipped', value: 'skipped' },
                { text: 'Completed', value: 'completed' },
                { text: 'Error', value: 'error' },
              ],
              onFilter: (value, record) => record.status === value,
            }
          : {}),
        render: (status: ExecutionItemStatuses, record: ExecutionItem) => {
          const statusConfig = {
            completed: { icon: <CheckCircleOutlined />, color: 'success' },
            pending: { icon: <ClockCircleOutlined />, color: 'orange' },
            'input needed': {
              icon: <ExclamationCircleOutlined />,
              color: 'warning',
            },
            skipped: { icon: <MinusCircleOutlined />, color: 'default' },
            invalid: { icon: <CloseCircleOutlined />, color: 'volcano' },
            error: { icon: <CloseCircleOutlined />, color: 'error' },
          } as const;

          if (
            record.id === currentActionItemId &&
            (source === ChangeRequestSource.ACTIONS_IMPLEMENT_ALL ||
              source === ChangeRequestSource.ACTIONS_IMPLEMENT_ONE) &&
            loading
          ) {
            return (
              <div ref={processingRowRef}>
                <Tag icon={<SyncOutlined spin />} color="processing">
                  PROCESSING
                </Tag>
              </div>
            );
          }

          const { icon, color } = statusConfig[status] ?? {
            icon: null,
            color: 'default',
          };

          return (
            <Tag icon={icon} color={color}>
              {status.toUpperCase()}
            </Tag>
          );
        },
      },
      {
        title: 'Links',
        key: 'links',
        dataIndex: 'links',
        width: (140 / 920) * remainingWidth,
        render: (links: ExecutionLink[]) => {
          return (
            <>
              <div className="links-wrapper">
                {links &&
                  links.map((link, index) => (
                    <p key={index}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {link.label}
                      </a>
                    </p>
                  ))}
              </div>
            </>
          );
        },
      },
    ];

    // Add "Actions" column only if it is not the summary step.
    if (!isSummaryStep) {
      actionColumns.push({
        title: 'Actions',
        key: 'actions',
        width: 75,
        render: (_, record, index) => (
          <div className="action-items">
            <Dropdown
              menu={{
                items: dropdownMenu(record, index),
              }}
              trigger={['click']}
            >
              <MoreOutlined
                className="dots-icon"
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        ),
      });
    }

    return actionColumns;
  }, [dropdownMenu, currentStep, isPrintView]);

  const resizableColumns = useResizableColumns(columns, 250);

  if ((!data || cr?.execution?.items?.length === 0) && !_loading && !_error) {
    return (
      <NoDataFound description='Click "Generate Action Items" to create action items' />
    );
  }

  if (_error && !_loading) {
    return <ErrorResult code={_error?.statusCode} subTitle={_error?.message} />;
  }

  const expandToFullScreen = () => {
    if (editingItem) {
      handleExpandToFullScreen(editingItem);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );
        const updatedDataSource = arrayMove(prevState, activeIndex, overIndex);
        updateDataArray({ items: updatedDataSource });
        return updatedDataSource;
      });
    }
  };

  return (
    <>
      {editingItem && (
        <EditChangeRequestAction
          editingRecord={editingItem}
          onClose={() => handleEditRow(null)}
          onNavigate={navigateToEdit}
          onSave={async (updatedItem, navigate) => {
            const result = await saveItem(updatedItem, navigate);
            return result as Result;
          }}
          itemCount={cr?.execution?.items.length || 0}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          expandToFullScreen={expandToFullScreen}
        />
      )}
      <div id="change-request-action" data-testid="change-request-action">
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={dataSource.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              ref={tblRef}
              columns={resizableColumns}
              rowKey={(record) => record.id}
              loading={_loading}
              tableLayout="auto"
              scroll={{ x: isPrintView ? '100%' : 1200 }}
              style={{ width: '100%' }}
              className={`${_loading ? 'table-spinner' : ''} ${
                editingItem && !_loading && !isImplementing
                  ? 'editing-item-table'
                  : ''
              } ${
                isSummaryStep || !!editingItem || isImplementing
                  ? ''
                  : 'dnd-table'
              }`}
              dataSource={dataSource || []}
              pagination={false}
              rowClassName={(record) =>
                isSameRecord(record, editingItem) ||
                (isSameRecord(record, currentActionItemId) && loading)
                  ? 'active-row'
                  : ''
              }
              onRow={(record) => {
                return {
                  onClick: () => {
                    editingItem && !loading && handleEditRow(record);
                  },
                };
              }}
              components={{
                header: { cell: ResizableTitle },
                body: {
                  row: (props: DraggableRowProps) => (
                    <DraggableRow
                      {...props}
                      isDnDDisabled={
                        isSummaryStep || !!editingItem || isImplementing
                      }
                    />
                  ),
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
};

export default ChangeRequestActions;
